import React, {useState, useMemo, useCallback, useEffect} from 'react';
import {isEmpty} from 'lodash';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import Env from '../../../../../../../util/env';
import {labels} from '../../../../../../../util/utils';
import {trialBalanceType, non_MEST} from '../../../../../../../util/uiconstants';
import {useUserPreferences, useEngagementId, useDocumentId} from '../../../../../../../util/customHooks';
import {useSelectedStEntityId, useSmartEvidenceSourceId} from '../../../../../../../util/Ares/customHooks';
import {getMateriality} from '../../../../../../../actions/Materiality/materialityactions';
import {getAccountById} from '../../../../../../../actions/Account/accountactions';
import {getCurrentMateriality} from '../../../../../../../selectors/Ares/materiality';
import Fetch from '../../../../../Fetch/fetch';
import Loader from '../../../../../LoadingIndicator/LoadingIndicator';
import AresSpecialBodyContainer from '../../AresSpecialBodyContainer';
import ViewAndEditAccount from './ViewAndEditAccount';
import HelixIntegrationModalContainer from '../../../../../../Main/Ares/Helix/HelixIntegrationModal/index';
import StEntityDropdown from '../../../../../MultiEntity/stEnityDropdown/StEntityDropdown';
import Button from '../../../../../BaseComponentWrappers/MotifButton';
import CreateEditAccounts from '../../../../../Accounts/CreateEditAccount/CreateEditAccounts';

const ViewAndEditAccountBody = ({
	bodyContent,
	headerId,
	sectionId,
	bodyId,
	updateBodyOptions,
	getBodiesBySectionId,
	isSnapshotView,
	showGuidance
}) => {
	const dispatch = useDispatch();

	const engagementId = useEngagementId();
	const documentId = useDocumentId();
	const smartEvidenceSourceId = useSmartEvidenceSourceId(documentId);
	const userPreferences = useUserPreferences();

	const account = useSelector((state) => state.account),
		engagement = useSelector((store) => store.engagement),
		materiality = useSelector(getCurrentMateriality),
		featureConfigurations = useSelector((store) => store.configurations);

	const [stEntity] = useSelectedStEntityId();

	const [isStEntityAvailable, isStEntityDropdownVisible] = useMemo(() => {
		const isAvailable =
			account.entities &&
			account.entities.length > 0 &&
			account.entities.find((entity) => entity.id === stEntity) !== undefined;

		const isVisible = account.entities && account.entities.length > 0;

		return [isAvailable, isVisible];
	}, [stEntity, account.entities]);

	const [isLoading, setIsLoading] = useState(true),
		[selectedEntityId, setSelectedEntityId] = useState(0);

	const trialBalance = useMemo(() => {
		return account.trialBalanceData?.find(
			(data) =>
				data.trialBalanceTypeId === trialBalanceType.Planning && data.stEntityId === (selectedEntityId ?? non_MEST)
		);
	}, [account.id, account.trialBalanceData, selectedEntityId]);

	const onEntitySelected = (stEntityId) => {
		setSelectedEntityId(stEntityId);
	};

	const handleOnClickNavigate = (e) => {
		e.preventDefault();
		const helixConfigurationsUrl = Env.getURL('hercules_web') + '/connections?engagementid=' + engagementId;
		window.open(helixConfigurationsUrl, '_blank');
	};

	const showViewAndEditAccount = useMemo(() => {
		return selectedEntityId === 0 || account?.entities?.find((entity) => entity.id === selectedEntityId) !== undefined;
	}, [selectedEntityId, account]);

	const fetch = useCallback(() => {
		setIsLoading(true);
		dispatch(getMateriality(engagementId)).then(() => {
			if (isEmpty(account) && smartEvidenceSourceId) {
				dispatch(getAccountById(engagementId, smartEvidenceSourceId)).then(() => setIsLoading(false));
			} else {
				setIsLoading(false);
			}
		});
	}, [engagementId, dispatch, account, smartEvidenceSourceId]);

	useEffect(() => {
		if (stEntity > 0) {
			const selectStEntity = isStEntityAvailable ? stEntity : 0;
			setSelectedEntityId(selectStEntity);
		}
	}, [isStEntityAvailable, stEntity]);

	return (
		<Fetch fetch={fetch}>
			<StyledViewAndEditAccountBody className="StyledViewAndEditAccountBody">
				<HelixIntegrationModalContainer>
					{isLoading ? (
						<Loader show label={labels.loading} view="inlineView" styleName="LoadingIndicator" />
					) : (
						<AresSpecialBodyContainer
							bodyContent={bodyContent}
							headerId={headerId}
							sectionId={sectionId}
							bodyId={bodyId}
							updateBodyOptions={updateBodyOptions}
							getBodiesBySectionId={getBodiesBySectionId}
							isSnapshotView={isSnapshotView}
							showGuidance={showGuidance}
							showDescription={false}
						>
							<section className="AccountFilterWrapper">
								{engagement?.isMultiEntityEngagement && isStEntityDropdownVisible && (
									<section className="StEntityDropdown">
										<StEntityDropdown
											stEntityId={selectedEntityId}
											onEntitySelected={onEntitySelected}
											isIndividualAccount
											entities={account.entities}
										/>
									</section>
								)}
								<section className="LinksWrapper">
									<section className="ManageAccountBtn">
										<Button
											variant="text-alt"
											className="ManageAccountMapping"
											onClick={handleOnClickNavigate}
											showLabel
											isEllipsed
											buttonLabel={labels.manageAccountMapping}
											noOfLines={1}
										/>
									</section>
									<span className="LinkSeparator">|</span>
									<CreateEditAccounts isEdit accountId={account?.id} onClose={() => {}} />
								</section>
							</section>
							{!isEmpty(account) && showViewAndEditAccount ? (
								<ViewAndEditAccount
									userPreferences={userPreferences}
									account={account}
									engagement={engagement}
									body={bodyContent}
									materiality={materiality}
									trialBalance={trialBalance}
									featureConfigurations={featureConfigurations}
									selectedEntityId={selectedEntityId}
								/>
							) : (
								<section className="noAccountRecords">{labels.noAccountAvailable}</section>
							)}
						</AresSpecialBodyContainer>
					)}
				</HelixIntegrationModalContainer>
			</StyledViewAndEditAccountBody>
		</Fetch>
	);
};

const StyledViewAndEditAccountBody = styled.section`
	width: 100%;
	margin-bottom: var(--px-24);
	.motif-progress-bar-wrapper.motif-progress-bar-wrapper-circle {
		position: relative;
		width: 100%;
	}
	.noAccountRecords {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		width: 100%;
		border-radius: var(--px-12);
		border: var(--px-1) solid var(--neutrals-200);
		background: var(--neutrals-00white);
		color: var(--neutrals-700);
		font-size: var(--px-14);
		font-weight: 300;
		line-height: var(--px-24);
		margin-bottom: var(--px-16);
		min-height: var(--px-184);
	}
	.LinkSeparator {
		margin: 0 var(--px-10);
		color: var(--blue-600);
		font-weight: bold;
		line-height: var(--px-22);
	}
	.AccountFilterWrapper {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		margin-bottom: var(--px-24);
		.AccountFilter {
			margin-right: var(--px-20);
			button {
				border-radius: var(--px-8);
				border-color: var(--neutrals-300);
				width: var(--px-54) !important;
				height: var(--px-54) !important;
				background-color: var(--neutrals-00white);
				svg {
					fill: var(--neutrals-900);
					path {
						fill: var(--neutrals-900);
					}
				}
			}
		}
		.AccountsSearchbox {
			.AccountsSearchWrapper {
				width: var(--px-320);
			}
		}
		.StEntityDropdown {
			width: var(--px-320);
		}
		.LinksWrapper {
			display: flex;
			align-items: center;
			margin-left: auto;
			.ManageAccountBtn {
				display: flex;
				align-items: center;
				width: auto;
				max-width: var(--px-220);
				height: 100%;
			}
			.LinkSeparator {
				margin: 0 var(--px-10);
				color: var(--blue-600);
				font-weight: bold;
				line-height: var(--px-22);
			}
		}
	}
`;

export default ViewAndEditAccountBody;
